import React from 'react';

import { GlobalAccountPersonOrgRole, Money } from '@ndvr/customer-api';
import { PortfolioProfile } from '@ndvr/portfolio-api';

import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { formatCurrency, getGlobalAccountRoleDisplayLabel, useHouseholdRoutePaths, useRequiredParams } from 'utils';
import { useGlobalAccount } from 'views/household/household-queries';
import { usePersonOrgFinancialProfile } from 'views/person/person-queries';
import { PortfolioProfileTable } from 'views/portfolio/PortfolioProfileTable';
import { HorizontalLayout, IconAward, Text, TextLink, Title, VerticalLayout } from 'widgets';

export const PortfoliosTab = () => {
    const { PortfolioOverviewTabRoutePath } = useHouseholdRoutePaths();
    const { personOrgGUID } = useRequiredParams(['personOrgGUID']);
    const { data: person } = usePersonOrgFinancialProfile(personOrgGUID);

    return (
        <VerticalLayout $gap={16}>
            <Title section>Households</Title>

            <VerticalLayout $gap={16}>
                {person?.profile.globalAccountPersonOrgRoles?.map((accountRole) => {
                    const portfolioProfilesForAccount =
                        person.portfolioProfiles?.filter(
                            (profile) => profile.portfolio.globalAccountGUID === accountRole.globalAccountGUID,
                        ) || [];
                    const aum = person.aumForAccounts && person.aumForAccounts[accountRole.globalAccountGUID];

                    return (
                        <HouseholdCard
                            key={accountRole.globalAccountPersonOrgRoleGUID}
                            accountRole={accountRole}
                            portfolioProfiles={portfolioProfilesForAccount}
                            aum={aum}
                            PortfolioOverviewTabRoutePath={PortfolioOverviewTabRoutePath}
                        />
                    );
                })}
            </VerticalLayout>
        </VerticalLayout>
    );
};

const HouseholdCard = ({
    accountRole,
    portfolioProfiles,
    aum,
    PortfolioOverviewTabRoutePath,
}: {
    accountRole: GlobalAccountPersonOrgRole;
    portfolioProfiles: PortfolioProfile[];
    aum: Money | undefined;
    PortfolioOverviewTabRoutePath: ReturnType<typeof useHouseholdRoutePaths>['PortfolioOverviewTabRoutePath'];
}) => {
    const { data: household } = useGlobalAccount(accountRole.globalAccountGUID);

    return (
        <Card>
            <CardTitle className="grid grid-cols-[562px_1fr_1fr] px-6 pt-6">
                <TextLink to={PortfolioOverviewTabRoutePath(accountRole.globalAccountGUID)}>
                    {household?.globalAccountNamePlain || 'Unnamed Household'}
                </TextLink>
                <HorizontalLayout $rowCenter $gap={4}>
                    <IconAward />
                    <Text>{getGlobalAccountRoleDisplayLabel(accountRole)}</Text>
                </HorizontalLayout>
                <Text className="self-center">{formatCurrency(aum?.amount, 'USD')}</Text>
            </CardTitle>
            <CardContent>
                <PortfolioProfileTable portfolioProfiles={portfolioProfiles} columnConfig={{ showClient: true }} />
            </CardContent>
        </Card>
    );
};
